import { DatabaseFieldType, ImportedFileType } from '@databases/types/database';
import { DatabaseFilterType } from '@databases/types/database/view';

import { TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH } from '../../constants';
import { getImageUrl } from '../../media-client';
import { selectBaseUrl } from '../../redux/context/selectors';
import { encodeHtml, isFile } from '../../utils';
import { FieldType } from '../field-types';

const attachmentFieldType: FieldType<DatabaseFieldType.ATTACHMENT> = {
  supportedFilterTypes: [DatabaseFilterType.EMPTY],

  supportedImportTypes: [ImportedFileType.HTML],

  serializeValue: () => undefined,

  deserializeValue: () => undefined,

  renderExportView: async (
    { field, value },
    state,
    renderContext,
    recursionCount,
    contentId
  ) => {
    if (value && 'attachmentId' in value) {
      const baseUrl = selectBaseUrl(state);
      return `<span class="confluence-embedded-file-wrapper"><img class="confluence-embedded-image" src="${encodeHtml(
        `${baseUrl}${value.path}`
      )}" width="${encodeHtml(
        field.width ?? TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH
      )}"/></span>`;

      // This would be the right way to embed the image, but in the export view, Confluence uses the content ID of the current page rather than the referenced content ID.
      // See https://community.developer.atlassian.com/t/wrong-attachment-is-referenced-by-ac-image-in-export-view/57791
      // return `<ac:image><ri:attachment ri:filename="${encodeHtml(getFilename(value))}"><ri:content-entity ri:content-id="${encodeHtml(value.containerId)}"/></ri:attachment></ac:image>`;
    } else if (value && 'fileId' in value) {
      const imageUrl = await getImageUrl(state, contentId, value.fileId);
      if (!imageUrl) {
        return '';
      }
      return `<span class="confluence-embedded-file-wrapper"><img class="confluence-embedded-image" src="${encodeHtml(
        `${imageUrl}`
      )}" width="${encodeHtml(
        field.width ?? TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH
      )}" data-image-id="${value.fileId}"/></span>`;
    } else {
      return '';
    }
  },

  parseFromHtml: (el) => {
    const img = el.find('img[src]').eq(0);
    if (img.length > 0) {
      return { src: img.attr('src') ?? '', fileId: img.data('image-id') ?? '' };
    }
  },

  getInitialValue: () => undefined,

  isEmptyValue: ({ value }) => !value,

  isEqualValue: ({ value: value1 }, { value: value2 }) => {
    if (value1 && value2) {
      if ('src' in value1 && 'src' in value2) {
        return value1.src === value2.src;
      } else if ('fileId' in value1 && 'fileId' in value2) {
        return value1.fileId === value2.fileId;
      }
    }
    return !value1 && !value2;
  },

  getStringRepresentation: ({ value }) => (!value ? '' : isFile(value) ? value.name : ''),

  getReferencedAttachments: ({ value }) => {
    return value && 'attachmentId' in value ? [value] : [];
  },

  noDefaultValue: true,
};

export default attachmentFieldType;
